import { useState } from "react";
import { Link } from "react-router-dom";
import { useSEO, BASE } from "@/lib/seo";
import { IMAGES } from "@/lib/images";
import ImageCredit from "@/components/site/ImageCredit";
import ArticleShell, { H2, P, SeeAlso } from "@/components/site/ArticleShell";
import { Reveal } from "@/components/site/Reveal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowUpRight } from "lucide-react";

import { RichText } from "@/components/site/RichText";

const COURANTS = [
  {
    id: "figuratif",
    name: "Figuratif contemporain",
    tag: "Le corps réinventé",
    text: "La figure humaine demeure le premier territoire de la sculpture. Mais là où le classique cherchait l'idéal, le figuratif contemporain assume la fragilité, la déformation, la fragmentation. Le corps devient un langage : il exprime la condition humaine plus qu'il ne la représente.",
    reperes: "Héritiers de Rodin et de Giacometti ; nombreux sculpteurs d'atelier français actuels.",
    materiaux: [
      { label: "Bronze", slug: "bronze" },
      { label: "Terre cuite" },
      { label: "Résine", slug: "resine" },
      { label: "Plâtre", slug: "platre" },
    ],
    img: IMAGES.figurative,
  },
  {
    id: "abstrait",
    name: "Abstraction",
    tag: "La forme pure",
    text: "L'abstraction sculpturale libère le volume de toute référence au réel : lignes, courbes, masses et vides se suffisent à eux-mêmes. Elle explore l'équilibre, la tension, le rythme — et dialogue souvent avec l'architecture et l'espace public.",
    reperes: "Constantin Brâncuși en ouvre la voie ; Bernar Venet et ses arcs d'acier en sont une figure française majeure.",
    materiaux: [
      { label: "Acier", slug: "metal" },
      { label: "Pierre", slug: "pierre" },
      { label: "Bronze poli", slug: "bronze" },
    ],
    img: IMAGES.abstractMetal,
  },
  {
    id: "pop",
    name: "Pop sculpture",
    tag: "L'objet du quotidien",
    text: "Issue du pop art et du nouveau réalisme français, la pop sculpture détourne les objets de la société de consommation : compressions, accumulations, emballages grossis. Colorée, ironique, immédiatement lisible, elle brouille la frontière entre art et objet design.",
    reperes: "César et ses compressions, Arman et ses accumulations, Niki de Saint Phalle et ses Nanas.",
    materiaux: [
      { label: "Résine", slug: "resine" },
      { label: "Objets assemblés" },
      { label: "Métal peint", slug: "metal" },
      { label: "Plastique" },
    ],
    img: IMAGES.popDog,
    example: {
      src: "/images/sculpture-pop-glace-resine.jpg",
      alt: "Exemple de pop sculpture : une glace géante sculptée en résine, icône pop détournée",
      caption: "Une glace géante sculptée en résine — l'icône pop détournée",
      link: "https://www.arson-sculptures.com/product-page/esculmaux-105-sculpture-contemporaine-pop-art",
      linkLabel: "Voir un exemple de sculpture pop en résine",
    },
  },
  {
    id: "urbain",
    name: "Art urbain",
    tag: "La rue comme atelier",
    text: "Né du graffiti et de la culture street, l'art urbain a conquis le volume : installations éphémères, sculptures murales, interventions dans l'espace public. Il revendique l'accessibilité et le dialogue direct avec les passants.",
    reperes: "Invader et ses mosaïques, les interventions monumentales dans les friches et quartiers.",
    materiaux: [
      { label: "Béton" },
      { label: "Métal", slug: "metal" },
      { label: "Mosaïque" },
      { label: "Matériaux de récupération" },
    ],
    img: IMAGES.graffiti,
  },
  {
    id: "monumental",
    name: "Monumental & in situ",
    tag: "L'échelle du paysage",
    text: "La sculpture monumentale pense l'œuvre à l'échelle de l'espace public ou du paysage. Commandes urbaines, parcs de sculptures, land art : l'œuvre modifie le lieu autant qu'elle l'occupe, et se vit en marchant autour.",
    reperes: "Jean Dubuffet et ses jardins d'émail, les grandes commandes du 1 % artistique, Xavier Veilhan.",
    materiaux: [
      { label: "Acier", slug: "metal" },
      { label: "Béton" },
      { label: "Bronze", slug: "bronze" },
      { label: "Matériaux composites" },
    ],
    img: IMAGES.retiro,
  },
  {
    id: "conceptuel",
    name: "Minimalisme & art conceptuel",
    tag: "L'idée avant la forme",
    text: "Ici, la matérialité de l'œuvre est subordonnée à une idée : l'installation, la série, le geste comptent autant que l'objet fini. La sculpture devient proposition, expérience de l'espace et du spectateur.",
    reperes: "Daniel Buren et son travail in situ, Jean-Michel Othoniel et ses verres de Murano.",
    materiaux: [
      { label: "Verre" },
      { label: "Lumière" },
      { label: "Structures modulaires" },
    ],
    img: IMAGES.panels,
  },
];

export default function CourantsPage() {
  useSEO(
    "Les Courants de la Sculpture Contemporaine : Figuratif, Abstrait, Pop, Monumental",
    "Panorama complet des mouvements sculpturaux contemporains : de la figuration expressive à l'art abstrait, pop art, art urbain et installations monumentales.",
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Les courants de la sculpture contemporaine en France",
      description:
        "Figuratif contemporain, abstraction, pop sculpture, art urbain, monumental et art conceptuel : les six grandes familles de la création actuelle.",
      inLanguage: "fr-FR",
      mainEntityOfPage: `${BASE}/courants-et-mouvements`,
      author: { "@type": "Organization", name: "Sculpteur Français", url: `${BASE}` },
      publisher: { "@type": "Organization", name: "Sculpteur Français", url: `${BASE}` },
    },
    { type: "article" }
  );
  const [active, setActive] = useState(COURANTS[0].id);

  return (
    <ArticleShell
      testId="courants-page"
      eyebrow="Panorama — les mouvements"
      titleLines={["Les courants de la sculpture", "contemporaine en France"]}
      lead="Figuratif, abstraction, pop sculpture, art urbain, monumental : les grandes familles qui structurent la création actuelle — et comment les reconnaître."
      image={IMAGES.museum}
    >
      <H2>Six familles pour s'orienter</H2>
      <P>
        Les frontières sont poreuses — un même sculpteur peut passer du
        figuratif à l'abstraction, de l'atelier à la rue. Mais ces six familles
        offrent une boussole pour lire la production actuelle.
      </P>

      <Tabs value={active} onValueChange={setActive} className="mt-10">
        <div data-testid="courants-tabs">
          <TabsList className="flex h-auto flex-wrap justify-start gap-2 rounded-none bg-transparent p-0" aria-label="Courants">
            {COURANTS.map((c) => (
              <TabsTrigger
                key={c.id}
                value={c.id}
                data-testid={`courant-tab-${c.id}`}
                className={`rounded-none border px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 data-[state=active]:shadow-none ${
                  active === c.id
                    ? "border-bronze bg-bronze text-paper data-[state=active]:bg-bronze data-[state=active]:text-paper"
                    : "border-ink/20 bg-transparent text-ink/70 hover:border-bronze hover:text-bronze"
                }`}
              >
                {c.name}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {COURANTS.map((c) => (
          <TabsContent key={c.id} value={c.id} className="mt-6">
            <div data-testid={`courant-panel-${c.id}`} className="border border-ink/15 bg-cream p-8 lg:p-12">
              <div className="grid items-center gap-10 lg:grid-cols-12">
                <div className="lg:col-span-7">
                  <p className="eyebrow">{c.tag}</p>
                  <h3 className="mt-3 font-serif text-3xl text-ink">{c.name}</h3>
                  <p className="mt-5 text-base font-light leading-relaxed text-body sm:text-lg">
                    <RichText text={c.text} />
                  </p>
                </div>
                <figure className="lg:col-span-5">
                  <div className="group img-content-wrap">
                    <img
                      src={c.img.src}
                      alt={c.img.alt}
                      loading="lazy"
                      data-testid={`courant-img-${c.id}`}
                      className="img-content border border-ink/15"
                    />
                  </div>
                  <figcaption className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-smoke">
                    {c.img.caption}
                    <ImageCredit image={c.img} testid={`credit-courant-${c.id}`} />
                  </figcaption>
                </figure>
              </div>
              <div className="mt-8 grid gap-6 border-t border-ink/10 pt-6 sm:grid-cols-2">
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-bronze">Repères</p>
                  <p className="mt-2 text-sm font-light leading-relaxed text-smoke">{c.reperes}</p>
                </div>
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-bronze">Matériaux associés</p>
                  <div className="mt-3 flex flex-wrap gap-2" data-testid={`courant-materiaux-${c.id}`}>
                    {c.materiaux.map((m) =>
                      m.slug ? (
                        <Link
                          key={m.label}
                          to={`/materiaux-et-techniques/${m.slug}`}
                          data-testid={`courant-mat-${c.id}-${m.slug}`}
                          className="border border-ink/20 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-ink/70 transition-colors duration-300 hover:border-bronze hover:text-bronze"
                        >
                          {m.label}
                        </Link>
                      ) : (
                        <span
                          key={m.label}
                          className="border border-ink/10 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-smoke/70"
                        >
                          {m.label}
                        </span>
                      )
                    )}
                  </div>
                </div>
              </div>

              {c.example && (
                <figure className="mt-10 grid items-center gap-8 border-t border-ink/10 pt-10 sm:grid-cols-2" data-testid="courant-example">
                  <div className="img-content-wrap">
                    <img
                      src={c.example.src}
                      alt={c.example.alt}
                      loading="lazy"
                      className="img-content border border-ink/15"
                    />
                  </div>
                  <div>
                    <p className="eyebrow">Un exemple du mouvement</p>
                    <figcaption className="mt-3 font-serif text-xl leading-snug text-ink">
                      {c.example.caption}
                    </figcaption>
                    <a
                      href={c.example.link}
                      target="_blank"
                      rel="noopener"
                      data-testid="courant-example-link"
                      className="group mt-4 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-bronze"
                    >
                      <span className="link-underline">{c.example.linkLabel}</span>
                      <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                  </div>
                </figure>
              )}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      <H2>Des frontières mouvantes</H2>
      <P>
        Classer sert à comprendre, pas à enfermer. La sculpture française
        actuelle se caractérise précisément par ses hybridations : un buste
        figuratif peut être coulé en résine teintée façon pop, une abstraction
        d'acier peut occuper le paysage façon land art. L'essentiel est de
        repérer l'intention de l'artiste — ce que l'œuvre cherche à dire, pas
        seulement ce qu'elle montre.
      </P>

      <SeeAlso
        links={[
          { to: "/definition-sculpteur-contemporain", label: "Qu'est-ce qu'un sculpteur contemporain ?" },
          { to: "/materiaux-et-techniques", label: "Matériaux et techniques" },
          { to: "/faq", label: "Questions fréquentes" },
        ]}
      />
    </ArticleShell>
  );
}
